/* ==========================================================================
   Boutique product page — gallery, variant chips, add to bag, reserve panel.
   Loaded by sections/boutique-product.liquid.

   Ella's own product-form element is not used here: it is not wired into
   any section in this theme and it depends on a cart-notification element
   that boutique templates do not render. Instead this posts to
   /cart/add.js directly and then opens Ella's real cart drawer through the
   header trigger, so the boutique shares one cart with the main store
   without depending on Ella's internal JS contracts.

   Everything degrades. With no script the form still posts to /cart/add
   the ordinary way and the browser lands on the cart.
   ========================================================================== */

(function () {
  'use strict';

  function money(cents, format) {
    var amount = (cents / 100).toFixed(2);
    var parts = amount.split('.');
    var whole = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
    return (format || 'R {{amount}}').replace('{{amount}}', whole + '.' + parts[1]);
  }

  /* --- gallery ---------------------------------------------------------

     The track is a horizontal scroll-snap strip, so swipe is the browser's
     own. This adds arrows (wrapping at both ends), left and right keys while
     the gallery has focus, dots or a counter, thumbnails, and a polite live
     status. Returns goToMedia(id) so a variant change can jump to its image.
     ------------------------------------------------------------------------ */

  function initGallery(root) {
    var gallery = root.querySelector('[data-bq-gallery]');
    var track = gallery && gallery.querySelector('[data-bq-track]');
    if (!track) return null;

    var slides = Array.prototype.slice.call(track.children);
    var count = slides.length;
    var current = 0;
    var reduceMotion = window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    var goTargets = Array.prototype.slice.call(gallery.querySelectorAll('[data-bq-goto]'));
    var counter = gallery.querySelector('[data-bq-count]');
    var status = gallery.querySelector('[data-bq-status]');

    // Load the neighbours ahead of time, so the next image is there when
    // the customer arrives at it. Lazy images in a horizontal scroller are
    // otherwise only fetched once they are scrolled into view.
    function warm(index) {
      [index - 1, index, index + 1].forEach(function (i) {
        var slide = slides[(i + count) % count];
        var img = slide && slide.querySelector('img[loading="lazy"]');
        if (img) img.loading = 'eager';
      });
    }

    function mark(index) {
      if (index === current && gallery.hasAttribute('data-bq-ready')) return;
      current = index;
      goTargets.forEach(function (el) {
        var on = Number(el.getAttribute('data-bq-goto')) === index;
        el.classList.toggle('bq-on', on);
        if (on) el.setAttribute('aria-current', 'true');
        else el.removeAttribute('aria-current');
      });
      if (counter) counter.textContent = index + 1;
      if (status) status.textContent = 'Image ' + (index + 1) + ' of ' + count;
      warm(index);
    }

    function goTo(index, instant) {
      if (!count) return;
      index = (index + count) % count;
      var left = slides[index].offsetLeft - track.offsetLeft;
      track.scrollTo({ left: left, behavior: instant || reduceMotion ? 'auto' : 'smooth' });
      mark(index);
    }

    // Keep the indicator honest when the customer swipes.
    var ticking = false;
    track.addEventListener('scroll', function () {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(function () {
        ticking = false;
        var width = track.clientWidth || 1;
        var index = Math.round(track.scrollLeft / width);
        if (index >= 0 && index < count) mark(index);
      });
    }, { passive: true });

    var prev = gallery.querySelector('[data-bq-prev]');
    var next = gallery.querySelector('[data-bq-next]');
    if (prev) prev.addEventListener('click', function () { goTo(current - 1); });
    if (next) next.addEventListener('click', function () { goTo(current + 1); });

    goTargets.forEach(function (el) {
      el.addEventListener('click', function () {
        goTo(Number(el.getAttribute('data-bq-goto')));
      });
    });

    gallery.addEventListener('keydown', function (event) {
      if (count < 2) return;
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        goTo(current - 1);
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        goTo(current + 1);
      }
    });

    // Snap back to the current slide if the frame changes width.
    window.addEventListener('resize', function () { goTo(current, true); });

    gallery.setAttribute('data-bq-ready', '');
    var start = Number(gallery.getAttribute('data-bq-start')) || 0;
    mark(start);
    warm(start);
    if (start) goTo(start, true);

    return function goToMedia(mediaId) {
      if (!mediaId) return;
      for (var i = 0; i < count; i++) {
        if (slides[i].getAttribute('data-bq-media-id') === String(mediaId)) {
          if (i !== current) goTo(i);
          return;
        }
      }
    };
  }

  function initProduct(root) {
    var goToMedia = initGallery(root);

    var dataEl = root.querySelector('[data-bq-variants]');
    if (!dataEl) return;

    var variants;
    try {
      variants = JSON.parse(dataEl.textContent);
    } catch (e) {
      return;
    }

    var form = root.querySelector('#bq-product-form');
    var idField = root.querySelector('[data-bq-variant-id]');
    var priceEl = root.querySelector('[data-bq-price]');
    var freeShip = root.querySelectorAll('[data-bq-free-ship]');
    var paidShip = root.querySelectorAll('[data-bq-paid-ship]');
    var addButton = root.querySelector('[data-bq-add]');
    var addLabel = root.querySelector('[data-bq-add-label]');
    var reserveVariant = root.querySelector('[data-bq-reserve-variant]');
    var soldSuffix = root.querySelector('[data-bq-soldout]');
    var oosFlag = root.querySelector('[data-bq-oos-flag]');
    var reserveBtn = root.querySelector('[data-bq-reserve]');
    var reserveBody = root.querySelector('[data-bq-reserve-body]');
    var reserveSend = root.querySelector('[data-bq-reserve-submit]');
    var reserveRequest = root.querySelector('[data-bq-reserve-request]');
    var reserveType = root.querySelector('[data-bq-reserve-type]');
    var reserveForm = root.querySelector('#bq-reserve-form');

    // Round 41. Both labels come from the button's own attributes, rendered
    // from the section settings. The label is never read back from the page:
    // after a sold-out variant it said "Sold out", and that stuck.
    var ADD_TEXT = (addButton && addButton.getAttribute('data-bq-add-text')) || 'Add to bag';
    var SOLDOUT_TEXT = (addButton && addButton.getAttribute('data-bq-soldout-text')) || 'Sold out';
    var SUFFIX_TEXT = (addButton && addButton.getAttribute('data-bq-soldout-suffix')) || 'Sold out';

    // The variant on show. Starts as the one the page was rendered with.
    var currentVariant = null;
    if (idField) {
      currentVariant = variants.filter(function (v) { return String(v.id) === idField.value; })[0] || null;
    }

    var optionGroups = Array.prototype.slice.call(root.querySelectorAll('[data-bq-option]'));

    // Each group carries its option's position in data-bq-option. A
    // one-value metal option is not rendered (the metafield row stands in
    // for it), so DOM order and option position can differ — match on the
    // attribute, and treat an unrendered option as matching anything.
    function selectedOptions() {
      return optionGroups.map(function (group) {
        var checked = group.querySelector('input:checked');
        return {
          position: Number(group.getAttribute('data-bq-option')),
          value: checked ? checked.value : null
        };
      });
    }

    function matchVariant(chosen) {
      return variants.filter(function (variant) {
        return chosen.every(function (choice) {
          return choice.value === null || variant.options[choice.position] === choice.value;
        });
      })[0];
    }

    // A chip is sold out when the variant it would select — that value,
    // with every other option as it stands — cannot be bought, or does not
    // exist. Same rule as the Liquid, recomputed on every change, so ring
    // sizes follow the metal.
    function paintChips(chosen) {
      optionGroups.forEach(function (group) {
        var position = Number(group.getAttribute('data-bq-option'));
        group.querySelectorAll('.bq-chip').forEach(function (chip) {
          var input = chip.querySelector('input');
          if (!input) return;
          var trial = chosen.map(function (choice) {
            return choice.position === position ? { position: position, value: input.value } : choice;
          });
          var candidate = matchVariant(trial);
          var soldOut = !candidate || !candidate.available;
          chip.classList.toggle('bq-chip--soldout', soldOut);
          var sr = chip.querySelector('[data-bq-chip-sr]');
          if (sr) sr.textContent = soldOut ? (candidate ? ', sold out' : ', unavailable') : '';
        });
      });
    }

    // Round 44: a sold-out variant turns the viewing request into an
    // enquiry, and the gallery carries the card's "Out of stock" flag.
    // Words come from the button's attributes (section settings). Only
    // labels and hidden values change: an open form stays open and keeps
    // what the customer typed.
    function paintRequest(available) {
      if (oosFlag) oosFlag.hidden = available;
      if (!reserveBtn) return;
      var mode = available ? 'reserve' : 'ask';
      reserveBtn.textContent = reserveBtn.getAttribute('data-bq-' + mode + '-text');
      if (reserveBody) reserveBody.placeholder = reserveBtn.getAttribute('data-bq-' + mode + '-note');
      if (reserveSend) reserveSend.textContent = reserveBtn.getAttribute('data-bq-' + mode + '-send');
      if (reserveRequest) reserveRequest.value = available ? 'Viewing request' : 'Enquiry — sold out';
      // Round 45: Enquiry type follows too, and the action's hash carries
      // the mode through Shopify's redirect to pick the confirmation.
      if (reserveType) reserveType.value = available ? 'Reserve to view in store' : 'Ask about this piece';
      if (reserveForm) {
        var action = reserveForm.getAttribute('action') || '';
        reserveForm.setAttribute('action', action.replace(/#.*$/, '') + '#bq-sent-' + (available ? 'view' : 'ask'));
      }
    }

    function paintStock(variant) {
      var available = !!(variant && variant.available);
      if (addButton) addButton.disabled = !available;
      if (addLabel) addLabel.textContent = available ? ADD_TEXT : SOLDOUT_TEXT;
      paintRequest(available);
      // Round 42: on the first option's label line, not a note beneath it.
      if (soldSuffix) {
        if (variant && !variant.available) {
          var text = SUFFIX_TEXT;
          if (variant.options.length > 1) text += ' (' + variant.title + ')';
          soldSuffix.textContent = ' · ' + text;
          soldSuffix.hidden = false;
        } else {
          soldSuffix.hidden = true;
        }
      }
    }

    function paint(fromCustomer) {
      var chosen = selectedOptions();

      // Reflect the choice in each group's label and chip states.
      optionGroups.forEach(function (group) {
        var checked = group.querySelector('input:checked');
        var label = group.querySelector('[data-bq-option-value]');
        if (label && checked) label.textContent = checked.value;
        group.querySelectorAll('.bq-chip').forEach(function (chip) {
          chip.classList.toggle('bq-on', chip.contains(checked));
        });
      });

      if (!optionGroups.length) return;

      paintChips(chosen);

      var variant = matchVariant(chosen);
      currentVariant = variant || null;

      if (!variant) {
        if (addButton) addButton.disabled = true;
        if (addLabel) addLabel.textContent = 'Unavailable';
        if (soldSuffix) soldSuffix.hidden = true;
        paintRequest(false);
        return;
      }

      if (idField) idField.value = variant.id;

      // Show the variant's own image, where one is set. Only on a change
      // the customer made — on load the section has already started the
      // gallery on the right image.
      if (fromCustomer && goToMedia && variant.featured_media) {
        goToMedia(variant.featured_media.id);
      }
      if (reserveVariant) reserveVariant.textContent = variant.title;
      if (reserveVariant) reserveVariant.value = variant.title;

      if (priceEl) {
        var html = '';
        if (variant.compare_at_price && variant.compare_at_price > variant.price) {
          html += '<s>' + money(variant.compare_at_price) + '</s> ';
        }
        html += money(variant.price);
        priceEl.innerHTML = html;
      }

      // Rounds 47-48: free insured delivery only at or above the threshold
      // (cents, from the section setting), for the variant now on show. The
      // accordion line and benefit line 1 carry [data-bq-free-ship]; line 1's
      // "Insured delivery" carries [data-bq-paid-ship] and shows below it.
      freeShip.forEach(function (el) {
        el.hidden = variant.price < Number(el.getAttribute('data-bq-threshold') || 0);
      });
      paidShip.forEach(function (el) {
        el.hidden = variant.price >= Number(el.getAttribute('data-bq-threshold') || 0);
      });

      paintStock(variant);

      // Keep the URL on the variant the customer is actually looking at, so
      // a shared link opens the same piece.
      if (window.history && window.history.replaceState) {
        var url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
      }
    }

    optionGroups.forEach(function (group) {
      group.addEventListener('change', function (event) {
        if (!event.target.matches('input[type="radio"]')) return;
        paint(true);
      });
    });

    /* --- add to bag ----------------------------------------------------- */

    if (form && addButton) {
      form.addEventListener('submit', function (event) {
        if (!window.fetch) return; // let the browser post it the old way

        event.preventDefault();
        addButton.disabled = true;
        if (addLabel) addLabel.textContent = 'Adding…';

        fetch(window.Shopify && window.Shopify.routes ? window.Shopify.routes.root + 'cart/add.js' : '/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [{ id: idField.value, quantity: 1 }] })
        })
          .then(function (response) {
            if (!response.ok) throw new Error('add failed');
            return response.json();
          })
          .then(function () {
            // The store's Google Ads conversion listener in theme.liquid
            // matches on a button whose text contains "ADD TO CART". The
            // boutique button reads "Add to bag" in sentence case, so it
            // would never match and the conversion would silently stop
            // being recorded on boutique product pages. Fire it here
            // instead. The listener cannot also fire on this button, so
            // there is no double count.
            if (typeof window.gtag === 'function') {
              window.gtag('event', 'conversion', {
                send_to: 'AW-659699370/96g9CIHK3ZMCEKrtyLoC'
              });
            }

            // Refresh the header count, then open Ella's real cart drawer.
            return fetch('/cart.js')
              .then(function (r) { return r.json(); })
              .then(function (cart) {
                document.querySelectorAll('[data-cart-count]').forEach(function (el) {
                  el.textContent = cart.item_count;
                });
                var trigger = document.querySelector('[data-cart-sidebar]');
                if (trigger) {
                  trigger.click();
                } else {
                  window.location.href = '/cart';
                }
              });
          })
          .catch(function () {
            form.submit(); // fall back to a normal post
          })
          .finally(function () {
            // Round 41: back to the state of the variant on show, from the
            // attributes — not unconditionally enabled, and not the label as
            // it read before the request.
            paintStock(currentVariant);
          });
      });
    }

    /* --- reserve panel -------------------------------------------------- */

    var reserveButton = root.querySelector('[data-bq-reserve]');
    var reservePanel = root.querySelector('[data-bq-reserve-panel]');

    if (reserveButton && reservePanel) {
      reserveButton.addEventListener('click', function () {
        reservePanel.hidden = false;
        var first = reservePanel.querySelector('input, textarea');
        if (first) first.focus();
      });
    }

    // A submission that came back with a message should show the panel.
    if (reservePanel && reservePanel.querySelector('.bq-form-status')) {
      reservePanel.hidden = false;
    }

    paint();
  }

  function boot(scope) {
    (scope || document).querySelectorAll('.boutique-product-section').forEach(initProduct);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { boot(); });
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', function (event) { boot(event.target); });
})();
